import React, { useEffect, useState, useRef } from 'react';
import { frontendDb as db } from '../lib/frontend-db';
import type { Font } from '../lib/frontend-db';

const DynamicFontLoader: React.FC = () => {
  const [lastFontHash, setLastFontHash] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const loadedFontsRef = useRef<Set<string>>(new Set());

  // Optimized font loading - only when actually needed
  const loadFonts = async () => {
    // Prevent multiple simultaneous calls
    if (isLoading) {
      console.log('🔄 Font loading already in progress, skipping...');
      return;
    }
    
    setIsLoading(true);
    try {
      const fontsData = await db.getFonts();
      
      // Create hash to detect real changes only
      const fontHash = fontsData.map(f => `${f.id}:${f.name}:${f.files.length}`).sort().join('|');
      
      // Only process if fonts actually changed
      if (fontHash !== lastFontHash && lastFontHash !== '') {
        console.log(`🔄 Font list changed: ${fontsData.length} fonts available`);
        setLastFontHash(fontHash);
        
        // Load new fonts only
        const newFonts = fontsData.filter(font => !loadedFontsRef.current.has(font.id));
        if (newFonts.length > 0) {
          console.log(`🆕 Loading ${newFonts.length} new fonts:`, newFonts.map(f => f.name));
          newFonts.forEach(font => {
            loadFont(font);
            loadedFontsRef.current.add(font.id);
          });
        }
      } else if (lastFontHash === '') {
        // First load
        console.log(`🎯 Initial font load: ${fontsData.length} fonts available`);
        setLastFontHash(fontHash);
        fontsData.forEach(font => {
          loadFont(font);
          loadedFontsRef.current.add(font.id);
        });
      }
    } catch (error) {
      console.error('❌ Error loading fonts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load fonts on mount and set up periodic check
  useEffect(() => {
    loadFonts();
    
    // Very infrequent polling - only for backup
    intervalRef.current = setInterval(loadFonts, 120000); // 2 minutes
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Event-based font loading for immediate updates
  useEffect(() => {
    const handleFontUpload = () => {
      console.log('🔄 Font upload event received, checking for new fonts...');
      // Reset hash to force reload
      setLastFontHash('');
      setTimeout(loadFonts, 1500); // Longer delay to ensure server processing
    };

    window.addEventListener('fontUploaded', handleFontUpload);
    return () => window.removeEventListener('fontUploaded', handleFontUpload);
  }, [loadFonts]);

  const loadFont = (font: Font) => {
    try {
      console.log(`🔄 Loading font "${font.name}" with files:`, font.files);
      const fontFaceCSS = createFontFaceCSS(font);
      injectFontCSS(font.id, fontFaceCSS);
      
      // Test font loading after a delay
      setTimeout(() => {
        testFontLoading(font.name);
      }, 200);
      
      console.log(`✅ Font "${font.name}" CSS injected`);
    } catch (error) {
      console.error(`❌ Failed to load font "${font.name}":`, error);
    }
  };

  const testFontLoading = (fontName: string) => {
    const testElement = document.createElement('div');
    testElement.style.fontFamily = `"${fontName}", monospace`;
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    testElement.style.fontSize = '20px';
    testElement.textContent = 'Test';
    
    document.body.appendChild(testElement);
    const width1 = testElement.offsetWidth;
    
    testElement.style.fontFamily = 'monospace';
    const width2 = testElement.offsetWidth;
    
    document.body.removeChild(testElement);
    
    const isLoaded = width1 !== width2;
    if (isLoaded) {
      console.log(`✅ Font "${fontName}" loaded successfully`);
    } else {
      console.warn(`⚠️ Font "${fontName}" might not have loaded properly`);
    }
  };

  const createFontFaceCSS = (font: Font): string => {
    const fontFaceRules = font.files
      .map(file => {
        const format = getFontFormat(file.type);
        const fontPath = `${window.location.origin}${file.path}`;
        return `url("${fontPath}") format("${format}")`;
      })
      .join(', ');

    return `
      @font-face {
        font-family: "${font.name}";
        src: ${fontFaceRules};
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
    `;
  };

  const getFontFormat = (type: string): string => {
    const formats: { [key: string]: string } = {
      'woff2': 'woff2',
      'woff': 'woff',
      'ttf': 'truetype',
      'otf': 'opentype'
    };
    return formats[type.toLowerCase()] || 'truetype';
  };

  const injectFontCSS = (fontId: string, css: string) => {
    // Remove existing style
    const existingStyle = document.getElementById(`font-${fontId}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new style
    const style = document.createElement('style');
    style.id = `font-${fontId}`;
    style.textContent = css;
    document.head.appendChild(style);
  };

  return null;
};

export default DynamicFontLoader;

